import { useState, useEffect, FC, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import Breadcrumb from "./Breadcrumb";
import SidebarItem from "./SidebarItem";
import { RootState } from "redux/store/store";
import HomeIcon from "assets/icons/home.svg?react";
import DevicehubIcon from "assets/icons/device_hub.svg?react";
import CalendarMonthIcon from "assets/icons/calendar_month.svg?react";
import ActivityIcon from "assets/icons/activity.svg?react";
import SettingsIcon from "assets/icons/settings.svg?react";
import { breakerListInterface, menuItems } from "typings/navbarBreakerList";
import useClickOutside from "hooks/useClickOutside";
import { collapseSidebar } from "redux/slices/sidebarSlice";
import t from "utils/i18n";

interface Props {
  depthStep?: number;
  depth?: number;
  breakerList?: breakerListInterface[];
}

const Sidebar: FC<Props> = ({ depthStep = 10, depth = 0, breakerList }) => {
  // taking sidebar state from redux store
  const sidebarRef = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();
  const isSidebarOpen = useSelector(
    (state: RootState) => state.sidebar.isSidebarOpen,
  );
  const [searchText, setSearchText] = useState("");

  useClickOutside([sidebarRef], () => {
    dispatch(collapseSidebar());
  });
  const filteredBreakerList = (() => {
    const list = searchText
      ? breakerList?.filter(
          (breaker) =>
            typeof breaker.title === "string" &&
            breaker.title.toLowerCase().includes(searchText.toLowerCase()),
        )
      : breakerList;

    if (!list) return list;

    // Preserve gateway appearance order, sort numerically by id within each gateway
    const gatewayOrder: string[] = [];
    list.forEach((b) => {
      if (!gatewayOrder.includes(b.gatewayId)) gatewayOrder.push(b.gatewayId);
    });

    return list.slice().sort((a, b) => {
      const gwDiff =
        gatewayOrder.indexOf(a.gatewayId) - gatewayOrder.indexOf(b.gatewayId);
      if (gwDiff !== 0) return gwDiff;
      return Number(a.id) - Number(b.id);
    });
  })();
  const [sidebarItems, setSidebarItems] = useState<menuItems>([
    {
      id: "home",
      title: t("sidebar.home"),
      mainItem: true,
      url: "/",
      icon: <HomeIcon />,
    },
    {
      id: "breakers",
      title: t("sidebar.breakers"),
      mainItem: true,
      url: "/breakers",
      searchbar: true,
      icon: <DevicehubIcon />,
      children: filteredBreakerList,
    },
    {
      id: "schedule",
      title: t("sidebar.schedule"),
      mainItem: true,
      url: "/schedule",
      icon: <CalendarMonthIcon id="Schedule-Icon" />,
    },
    {
      id: "activity",
      title: t("sidebar.activity"),
      mainItem: true,
      url: "/activity",
      icon: <ActivityIcon />,
    },
    {
      id: "settings",
      title: t("sidebar.settings"),
      mainItem: true,
      url: "/settings",
      icon: <SettingsIcon />,
    },
  ]);

  useEffect(() => {
    setSidebarItems([
      {
        id: "home",
        title: t("sidebar.home"),
        mainItem: true,
        url: "/",
        icon: <HomeIcon />,
      },
      {
        id: "breakers",
        title: t("sidebar.breakers"),
        mainItem: true,
        url: "/breakers",
        searchbar: true,
        icon: <DevicehubIcon />,
        children: filteredBreakerList,
      },
      {
        id: "schedule",
        title: t("sidebar.schedule"),
        mainItem: true,
        url: "/schedule",
        icon: <CalendarMonthIcon id="Schedule-Icon" />,
      },
      {
        id: "activity",
        title: t("sidebar.activity"),
        mainItem: true,
        url: "/activity",
        icon: <ActivityIcon />,
      },
      {
        id: "settings",
        title: t("sidebar.settings"),
        mainItem: true,
        url: "/settings",
        icon: <SettingsIcon />,
      },
    ]);
  }, [breakerList, searchText, isSidebarOpen]); // <-- include searchText here

  return (
    <>
      {/* fixed Breadcrumb */}
      <Breadcrumb />

      {/* sidebar */}
      <div
        ref={sidebarRef}
        className={`${
          isSidebarOpen ? "w-[240px] drop-shadow-lg" : "w-[52px]"
        } fixed z-30 top-[93px] h-screen bg-elevation-1
        duration-200`}
      >
        <div className="hover:overflow-y-auto overflow-x-hidden h-[90%] scroll-slim">
          <ul className="flex flex-col items-center justify-center">
            {sidebarItems.map((menuItem, index) => {
              return (
                <li className="w-full" key={`${menuItem.id}${index}`}>
                  <SidebarItem
                    depthStep={depthStep}
                    depth={depth}
                    menuItem={menuItem}
                    isSidebarOpen={isSidebarOpen}
                    searchText={searchText}
                    onSearchChange={setSearchText}
                  />
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
